/**
 * Fleet Health desk (Page169 residual / page141 / page69).
 * Operator copy is path-free. Seat posture drives register vs ops metrics;
 * global metrics remain gated on the backend (ops dashboard entitlement).
 */
import { useMemo, useState } from "react";
import { html, M, _F, Ico, Card, Alert, Ticker, SOFT, go, KV } from "./ui.js";
import { path, useApi } from "./api.js";
import { useSession } from "./session.js";
import { useGlobalMetricsLive } from "./metrics-live.js";
import { fleetHealthBlocksOf } from "./fleet-live.js";

/** Path-free operator copy for Health desk failures. */
export function softFleetHealthErrorOf(err) {
  const n = String(err == null ? "" : err).trim();
  const low = n.toLowerCase();
  if (!low) return "That action could not be completed.";
  if (/\b403\b|forbidden|superadmin or entitled|not_authorized|denied/.test(low)) {
    return "Your account cannot open fleet health diagnostics. Ask a manager if you need access.";
  }
  if (/\b401\b|session|unauthorized|missing bearer/.test(low)) {
    return "Sign in again to continue.";
  }
  if (/\b404\b|not found/.test(low)) {
    return "That health bus is not available.";
  }
  if (/\b429\b|too many/.test(low)) {
    return "Too many tries — wait a moment, then retry.";
  }
  if (/\b503\b|unavailable|offline/.test(low)) {
    return "Health metrics are not ready right now.";
  }
  if (low === "ops_dashboard_seat_closed") {
    return "Queue and broker tiles need an operations metrics seat.";
  }
  if (/seat_closed|seat_gated|_admin_only/.test(low)) {
    return "Your account cannot open fleet health diagnostics. Ask a manager if you need access.";
  }
  if (/network|failed to fetch|timeout/.test(low)) {
    return "Network issue — try again in a moment.";
  }
  if (/\/api\/|\/fleet\/|\/admin\/|global-metrics|GET |POST |status_path|http/.test(low)) {
    return "That action could not be completed.";
  }
  if (/^[a-z][a-z0-9_]{3,}$/.test(n) && n.includes("_")) {
    return "Your account cannot open fleet health diagnostics. Ask a manager if you need access.";
  }
  if (n.length > 160) return n.slice(0, 157) + "…";
  return n;
}

/** Pure: register counts from devices + alarms packs. */
export function fleetHealthRegisterOf(devicesPack, alarmsPack) {
  const items = (devicesPack && (devicesPack.items || devicesPack.devices)) || [];
  const rows = Array.isArray(items) ? items : [];
  const online = rows.filter((d) => {
    if (!d) return false;
    if (d.is_online === true || d.online === true) return true;
    if (d.presence === "online") return true;
    return String(d.status || "").toLowerCase() === "online";
  }).length;
  const alarms = (alarmsPack && (alarmsPack.items || alarmsPack.alarms)) || [];
  const alarmN = Array.isArray(alarms) ? alarms.length : 0;
  return {
    devices: rows.length,
    online,
    quiet: Math.max(0, rows.length - online),
    alarms24h: alarmN,
  };
}

/**
 * Pure: scope + seat chips from Live posture.
 * Fail-closed when booleans are missing (loading / partial).
 */
export function fleetHealthSeatOf(me, post) {
  const p = post && typeof post === "object" ? post : {};
  const metricsEligible = p.metrics_eligible === true;
  const role = String(p.principal_role || (me && me.role) || "");
  const canSaScope = p.sa_scope_allowed === true;
  const registerAllowed = p.register_allowed === true;
  return { metricsEligible, canSaScope, role, registerAllowed };
}

export function FleetHealthDesk({ toast }) {
  const { me } = useSession();
  const posture = useApi(path("get__fleet_health_status"));
  const post = posture.data || {};
  /* Fail-closed while posture loads — no Soft invent open seats from me.features. */
  const seat = fleetHealthSeatOf(
    me,
    posture.loading ? null : post,
  );
  const [scope, setScope] = useState("admin");
  const metrics = useGlobalMetricsLive(
    seat.canSaScope ? scope : "admin",
    20000,
    seat.metricsEligible,
  );
  const devices = useApi(seat.registerAllowed ? path("get__devices") : "");
  const alarms = useApi(
    seat.registerAllowed
      ? path("get__alarms", { query: { since_hours: 24, limit: 100 } })
      : "",
  );
  const blocks = useMemo(() => fleetHealthBlocksOf(metrics.data), [metrics.data]);
  const reg = useMemo(
    () => fleetHealthRegisterOf(devices.data, alarms.data),
    [devices.data, alarms.data],
  );
  const err = metrics.error || devices.error || alarms.error;
  const transport = metrics.transport || "off";
  const scopeLabel = seat.metricsEligible
    ? (seat.canSaScope ? scope : "admin")
    : "off";
  const detail = post.detail || null;

  const reRead = () => {
    posture.reload && posture.reload();
    seat.metricsEligible && metrics.reload && metrics.reload();
    devices.reload && devices.reload();
    alarms.reload && alarms.reload();
    toast && toast("Fleet health refreshed", "go");
  };

  if (!posture.loading && post.enabled !== true) {
    return html`<div className="fleet-health-desk fleet-health-desk--liquid"
      data-cmc-page="page141-health" data-cmc-legacy="page69-health"
      data-cmc-residual="page169-health">
      <div className="fleet-health-desk__rail">
        <div>
          <p className="eyebrow">Fleet · Health</p>
          <h2 className="fleet-health-desk__title">Seat closed</h2>
          <p className="note">Your seat cannot open fleet health on this grant.</p>
        </div>
        <div className="btn-row fleet-health-desk__acts">
          <button type="button" className="btn btn--ghost btn--sm" disabled=${posture.loading}
            onClick=${reRead}><${Ico} d="refresh" w=${14}/> Refresh</button>
        </div>
      </div>
      <${Card} title="Your seat" meta=${post.posture_label || "Seat closed"}>
        <p className="note">${post.posture_line || softFleetHealthErrorOf(detail) || "Seat closed"}</p>
      <//>
    </div>`;
  }

  return html`<div className="fleet-health-desk fleet-health-desk--liquid"
    data-cmc-page="page141-health" data-cmc-legacy="page69-health"
    data-cmc-residual="page169-health">
    <div className="fleet-health-desk__rail">
      <div>
        <p className="eyebrow">Fleet · Health</p>
        <h2 className="fleet-health-desk__title">Measured signals</h2>
        <p className="note">
          ${post.posture_line || "Device counts when register is open. Queue and broker tiles only when ops metrics are open for this seat."}
        </p>
      </div>
      <div className="btn-row fleet-health-desk__acts">
        <button type="button" className="btn btn--ghost btn--sm" onClick=${reRead}
          disabled=${posture.loading}>
          <${Ico} d="refresh" w=${14}/> Refresh
        </button>
        <button type="button" className="btn btn--ghost btn--sm"
          onClick=${() => go("/fleet/diagnose")}>Why offline</button>
        <button type="button" className="btn btn--ghost btn--sm"
          onClick=${() => go("/ops")}>Ops map</button>
      </div>
    </div>

    <${Alert} tone="info" title="Fleet health"
      say="This desk shows the live device register and, when your seat allows it, platform queue and broker signals. Empty tiles stay empty."/>

    <${Card} title="Your seat" meta=${post.posture_label || (seat.metricsEligible ? "Metrics open" : "Survey only")}>
      <div className="btn-row" style=${{ flexWrap: "wrap", gap: "8px 14px" }}>
        <${KV} k="register" v=${seat.registerAllowed ? "open" : "closed"}/>
        <${KV} k="global metrics" v=${seat.metricsEligible ? "open" : "closed"}/>
        <${KV} k="SA scope" v=${seat.canSaScope ? "open" : "closed"}/>
        <${KV} k="transport" v=${transport}/>
      </div>
      ${detail ? html`<p className="note" style=${{ marginTop: 8 }}>${post.posture_line || detail}</p>` : null}
    <//>

    ${!seat.metricsEligible ? html`
      <${Alert} tone="warn" title="Global metrics not open for this seat"
        say="Device and alarm counts stay available from the register. Queue and broker tiles stay blank."/>`
    : null}
    ${err ? html`
      <${Alert} tone="warn" title="Could not load health signals"
        say=${softFleetHealthErrorOf(err)}/>` : null}

    <div className="fleet-health-desk__meta">
      <span data-on=${transport !== "off" ? "1" : "0"}>transport · ${transport}</span>
      <span>scope · ${scopeLabel}</span>
      <span>register · ${reg.devices}</span>
      <span>online · ${reg.online}</span>
      <span>alarms 24h · ${reg.alarms24h}</span>
    </div>

    ${seat.canSaScope ? html`
      <div className="chips" style=${{ marginBottom: 4 }}>
        ${[["admin", "Business-safe"], ["sa", "SA diagnostics"]].map(([id, l]) => html`
          <button key=${id} type="button" className="chip" data-tone="ai"
            data-sel=${scope === id ? "1" : "0"}
            onClick=${() => setScope(id)}>${l}</button>`)}
      </div>
      <p className="note" style=${{ margin: "0 0 10px" }}>
        Scope flips the live global-metrics query — not a second invented board.
      </p>` : null}

    <${Card} title="Device register" meta="devices + alarms (24h)"
        sub="Live on every seat with register access. Empty register stays empty.">
      <div className="fleet-health-desk__kpis" role="group" aria-label="Register KPIs">
        ${[
          ["Devices", reg.devices, "1"],
          ["Online", reg.online, reg.online ? "1" : "0"],
          ["Quiet", reg.quiet, reg.quiet ? "1" : "0"],
          ["Alarms 24h", reg.alarms24h, reg.alarms24h ? "1" : "0"],
        ].map(([k, v, on]) => html`
          <div key=${k} className="fleet-health-desk__kpi" data-on=${on}
            data-tone=${k === "Quiet" && reg.quiet ? "off" : (k === "Alarms 24h" && reg.alarms24h ? "alarm" : "")}>
            <span>${k}</span>
            <b><${Ticker} value=${v}/></b>
            <em>Live register</em>
          </div>`)}
      </div>
    <//>

    <${Card} title="Ops global metrics"
        meta=${seat.metricsEligible
          ? ("transport " + transport + " · scope " + scopeLabel)
          : "seat closed"}
        sub="Stream when available. Absent depths stay blank.">
      ${!seat.metricsEligible ? html`
        <p className="note">
          Queue and broker tiles below stay blank while global metrics are closed for this seat.
        </p>` : null}
      <div className="fleet-health-desk__blocks">
        ${blocks.map((b) => html`
          <${M.div} key=${b.id} className="hb glass fleet-health-desk__block"
              initial=${{ opacity: 0, y: 10 }} animate=${{ opacity: 1, y: 0 }}
              transition=${SOFT}>
            <div className="hb__in">
              <span className="hb__t">${b.t}</span>
              <b data-tone=${b.tone}>${b.v}</b>
              <em>${b.sub}</em>
            </div>
          <//>`)}
      </div>
    <//>

    <p className="note">
      Measured signals or an explicit blank. Domains and federation live under Platform control — not here.
    </p>
  </div>`;
}
